import {
  AREA_CENTER_X,
  AREA_CENTER_Y,
  AREA_MAX_X,
  AREA_MIN_X,
  HEATMAP_COLORS,
  INNER_BOTTOM_ROW_MAX_Y,
  INNER_BOTTOM_ROW_MIN_Y,
  INNER_CENTER_COLUMN_MAX_X,
  INNER_CENTER_COLUMN_MIN_X,
  INNER_LEFT_COLUMN_MAX_X,
  INNER_LEFT_COLUMN_MIN_X,
  INNER_MIDDLE_ROW_MAX_Y,
  INNER_MIDDLE_ROW_MIN_Y,
  INNER_RIGHT_COLUMN_MAX_X,
  INNER_RIGHT_COLUMN_MIN_X,
  INNER_TOP_ROW_MAX_Y,
  INNER_TOP_ROW_MIN_Y,
  OUTER_EDGE_BOTTOM_MAX_Y,
  OUTER_EDGE_BOTTOM_MIN_Y,
  OUTER_EDGE_LEFT_BOUNDARY_X,
  OUTER_EDGE_RIGHT_BOUNDARY_X,
  OUTER_EDGE_TOP_MAX_Y,
  OUTER_EDGE_TOP_MIN_Y,
} from "./constants";
import { BackgroundColors, Points } from "./index";

/**
 * Determines which zone a point belongs to based on the baseball strike zone mapping.
 *
 * The coordinate system uses:
 * - X and Y range from -10 to 10 (backend coordinates)
 * - Origin (0,0) is at the center of home plate
 * - Positive Y is up (towards pitcher), negative Y is down (towards catcher)
 * - Positive X is to the right (from catcher's perspective), negative X is to the left
 *
 * Zone priority order:
 * 1. Inner zones (checked first) - the main strike zone areas
 * 2. Outer zones (fallback) - areas outside the main strike zone
 *
 * @param x - X coordinate from backend (-10 to 10)
 * @param y - Y coordinate from backend (-10 to 10)
 * @returns The zone key that matches the BackgroundColors interface, or null if outside all zones
 *
 * @example
 * ```typescript
 * getZoneForPoint(0, 0)      // Returns "innerMiddleCenter" (center of strike zone)
 * getZoneForPoint(-5, 3)     // Returns "innerTopLeft"
 * getZoneForPoint(-8, 8)     // Returns "outerTopLeft"
 * getZoneForPoint(15, 15)    // Returns null (outside coordinate system)
 * ```
 */
export function getZoneForPoint(
  x: number,
  y: number,
): keyof BackgroundColors | null {
  // top row
  if (y >= INNER_TOP_ROW_MIN_Y && y <= INNER_TOP_ROW_MAX_Y) {
    if (x >= INNER_LEFT_COLUMN_MIN_X && x <= INNER_LEFT_COLUMN_MAX_X)
      return "innerTopLeft";
    if (x >= INNER_CENTER_COLUMN_MIN_X && x <= INNER_CENTER_COLUMN_MAX_X)
      return "innerTopCenter";
    if (x >= INNER_RIGHT_COLUMN_MIN_X && x <= INNER_RIGHT_COLUMN_MAX_X)
      return "innerTopRight";
  }

  // center row
  if (y >= INNER_MIDDLE_ROW_MIN_Y && y <= INNER_MIDDLE_ROW_MAX_Y) {
    if (x >= INNER_LEFT_COLUMN_MIN_X && x <= INNER_LEFT_COLUMN_MAX_X)
      return "innerMiddleLeft";
    if (x >= INNER_CENTER_COLUMN_MIN_X && x <= INNER_CENTER_COLUMN_MAX_X)
      return "innerMiddleCenter";
    if (x >= INNER_RIGHT_COLUMN_MIN_X && x <= INNER_RIGHT_COLUMN_MAX_X)
      return "innerMiddleRight";
  }

  // bottom row
  if (y >= INNER_BOTTOM_ROW_MIN_Y && y <= INNER_BOTTOM_ROW_MAX_Y) {
    if (x >= INNER_LEFT_COLUMN_MIN_X && x <= INNER_LEFT_COLUMN_MAX_X)
      return "innerBottomLeft";
    if (x >= INNER_CENTER_COLUMN_MIN_X && x <= INNER_CENTER_COLUMN_MAX_X)
      return "innerBottomCenter";
    if (x >= INNER_RIGHT_COLUMN_MIN_X && x <= INNER_RIGHT_COLUMN_MAX_X)
      return "innerBottomRight";
  }

  // Outer zones (edges)
  // Top edge zones
  if (y >= OUTER_EDGE_TOP_MIN_Y && y <= OUTER_EDGE_TOP_MAX_Y) {
    if (x >= AREA_MIN_X && x < OUTER_EDGE_LEFT_BOUNDARY_X)
      return "outerTopLeft";
    if (x > OUTER_EDGE_RIGHT_BOUNDARY_X && x <= AREA_MAX_X)
      return "outerTopRight";
  }
  // Bottom edge zones
  if (y >= OUTER_EDGE_BOTTOM_MIN_Y && y < OUTER_EDGE_BOTTOM_MAX_Y) {
    if (x >= AREA_MIN_X && x < OUTER_EDGE_LEFT_BOUNDARY_X)
      return "outerBottomLeft";
    if (x > OUTER_EDGE_RIGHT_BOUNDARY_X && x <= AREA_MAX_X)
      return "outerBottomRight";
  }

  // Outer top zones
  if (y > AREA_CENTER_Y) {
    if (x >= AREA_MIN_X && x <= AREA_CENTER_X) return "outerTopLeft";
    if (x >= AREA_CENTER_X && x <= AREA_MAX_X) return "outerTopRight";
  }

  // Outer bottom zones
  if (y < AREA_CENTER_Y) {
    if (x >= AREA_MIN_X && x <= AREA_CENTER_X) return "outerBottomLeft";
    if (x >= AREA_CENTER_X && x <= AREA_MAX_X) return "outerBottomRight";
  }

  return null;
}

/**
 * Maps a percentage value to a heat map color using a predefined color scale.
 *
 * The color scale represents data density/intensity:
 * - Lower percentages: Cool colors (blues/purples) indicating lower activity
 * - Higher percentages: Warm colors (reds) indicating higher activity
 *
 * @param percentage - The percentage value (0-100) representing data density in a zone
 * @returns A hex color string corresponding to the percentage range
 *
 * @example
 * ```typescript
 * getColorForPercentage(0)    // Returns "#c4cdf6" (light blue, no data)
 * getColorForPercentage(10)   // Returns "#1C066B" (dark blue, low activity)
 * getColorForPercentage(35)   // Returns "#7957F2" (purple, medium activity)
 * getColorForPercentage(80)   // Returns "#EA0E11" (red, high activity)
 * ```
 *
 * Color scale ranges:
 * - 0%: #c4cdf6 (light blue - default/empty)
 * - 1-15%: #1C066B (dark blue - very low)
 * - 16-30%: #481FDA (purple - low)
 * - 31-45%: #7957F2 (light purple - medium-low)
 * - 46-60%: #FD8789 (light red - medium-high)
 * - 61-75%: #DA4346 (medium red - high)
 * - 76-100%: #EA0E11 (dark red - very high)
 */
export function getColorForPercentage(percentage: number): string {
  if (percentage === 0) return HEATMAP_COLORS.EMPTY; // Default color for empty zones
  if (percentage <= 15) return HEATMAP_COLORS.VERY_LOW;
  if (percentage <= 30) return HEATMAP_COLORS.LOW;
  if (percentage <= 45) return HEATMAP_COLORS.MEDIUM_LOW;
  if (percentage <= 60) return HEATMAP_COLORS.MEDIUM_HIGH;
  if (percentage <= 75) return HEATMAP_COLORS.HIGH;
  return HEATMAP_COLORS.VERY_HIGH; // 76-100%
}

/**
 * Calculates the background colors for each zone based on point distribution percentages.
 *
 * This function processes all points across different colored datasets, determines which
 * zone each point belongs to, calculates the percentage distribution, and maps those
 * percentages to heat map colors.
 *
 * Algorithm:
 * 1. Initialize counters for all 13 zones (9 inner + 4 outer)
 * 2. Iterate through all points in all color datasets
 * 3. Use getZoneForPoint() to categorize each point
 * 4. Calculate percentage for each zone (count/total * 100)
 * 5. Map percentages to colors using getColorForPercentage()
 *
 * @param points - Object containing arrays of points organized by color/dataset
 *                Each key represents a color/dataset, each value is an array of {x, y} coordinates
 * @returns BackgroundColors object with hex color values for each zone
 *
 * @example
 * ```typescript
 * const points = {
 *   black: [
 *     { x: -4, y: 1 },    // -> innerMiddleLeft zone
 *     { x: 0, y: 1 },     // -> innerMiddleCenter zone
 *     { x: 0, y: 1 },     // -> innerMiddleCenter zone (duplicate)
 *     { x: 4, y: 1 },     // -> innerMiddleRight zone
 *     { x: -8, y: 5 },    // -> outerTopLeft zone
 *     { x: 8, y: -5 },    // -> outerBottomRight zone
 *   ]
 * };
 * const colors = calculateZoneColors(points);
 * // Result:
 * // colors.innerMiddleCenter = '#7957F2' (33.3% - 2 points out of 6)
 * // colors.innerMiddleLeft = '#481FDA' (16.7% - 1 point out of 6)
 * // colors.innerMiddleRight = '#481FDA' (16.7% - 1 point out of 6)
 * // colors.outerTopLeft = '#481FDA' (16.7% - 1 point out of 6)
 * // colors.outerBottomRight = '#481FDA' (16.7% - 1 point out of 6)
 * // All other zones = '#c4cdf6' (0% - no points)
 * ```
 *
 * @see getZoneForPoint For zone classification logic
 * @see getColorForPercentage For color mapping logic
 */
/**
 * Maps API zone numbers to UI zone property names and formats values based on chart type.
 *
 * The API returns zone data as numbered zones (1-13), while the UI expects named zones.
 * This function converts the API format to the UI format and formats the values appropriately
 * based on the chart type.
 *
 * Zone mapping:
 * - 1: innerTopLeft      - 6: innerMiddleLeft     - 11: outerTopLeft
 * - 2: innerTopCenter    - 7: innerMiddleCenter   - 12: outerTopRight
 * - 3: innerTopRight     - 8: innerMiddleRight    - 13: outerBottomLeft
 * - 4: innerBottomLeft   - 9: innerBottomCenter  - 10: outerBottomRight
 * - 5: innerBottomRight  - 10: innerBottomRight
 *
 * @param zoneData - Object with zone numbers as keys and rate values as values
 * @param chartType - Type of chart to determine formatting (affects suffix)
 * @returns Object with UI zone names as keys and formatted value strings
 *
 * @example
 * ```typescript
 * const apiData = {
 *   "1": 0.083,
 *   "2": 0.0,
 *   "7": 0.167
 * };
 *
 * // For rate-based charts (contact_rate_by_zone, whiff_rate_by_zone, hit_rate_by_zone)
 * const result = mapZoneDataToUIProps(apiData, "contact_rate_by_zone");
 * // Returns:
 * // {
 * //   innerTopLeft: "8%",
 * //   innerTopCenter: "0%",
 * //   innerMiddleCenter: "17%",
 * //   // ... other zones default to "0%"
 * // }
 *
 * // For avg_exit_velocity_by_zone
 * const velocityData = { "1": 95.5, "7": 88.2 };
 * const result2 = mapZoneDataToUIProps(velocityData, "avg_exit_velocity_by_zone");
 * // Returns:
 * // {
 * //   innerTopLeft: "96",
 * //   innerMiddleCenter: "88",
 * //   // ... other zones default to "0"
 * // }
 * ```
 */
export function mapZoneDataToUIProps(
  zoneData: Record<string, number>,
  chartType: string,
): Record<keyof BackgroundColors, string> {
  // Zone number to UI property mapping
  const zoneMapping: Record<string, keyof BackgroundColors> = {
    "1": "innerTopLeft",
    "2": "innerTopCenter",
    "3": "innerTopRight",
    "4": "innerBottomLeft",
    "5": "innerBottomRight",
    "6": "innerMiddleLeft",
    "7": "innerMiddleCenter",
    "8": "innerMiddleRight",
    "9": "innerBottomCenter",
    "10": "outerBottomRight",
    "11": "outerTopLeft",
    "12": "outerTopRight",
    "13": "outerBottomLeft",
  };

  // Determine if this is a rate-based chart (needs percentage suffix)
  const isRateChart = [
    "contact_rate_by_zone",
    "whiff_rate_by_zone",
    "hit_rate_by_zone",
  ].includes(chartType);
  const defaultValue = isRateChart ? "0%" : "0";

  // Initialize all zones with appropriate default value
  const result: Record<keyof BackgroundColors, string> = {
    outerTopLeft: defaultValue,
    outerTopRight: defaultValue,
    outerBottomLeft: defaultValue,
    outerBottomRight: defaultValue,
    innerTopLeft: defaultValue,
    innerTopCenter: defaultValue,
    innerTopRight: defaultValue,
    innerMiddleLeft: defaultValue,
    innerMiddleCenter: defaultValue,
    innerMiddleRight: defaultValue,
    innerBottomLeft: defaultValue,
    innerBottomCenter: defaultValue,
    innerBottomRight: defaultValue,
  };

  // Map zone data to UI properties
  Object.entries(zoneData).forEach(([zoneNumber, value]) => {
    const uiZone = zoneMapping[zoneNumber];
    if (uiZone) {
      if (isRateChart) {
        // For rate-based charts, convert to percentage
        const percentage = Math.round(value * 100);
        result[uiZone] = `${percentage}%`;
      } else {
        // For avg_exit_velocity_by_zone, use raw value rounded to whole number
        const roundedValue = Math.round(value);
        result[uiZone] = `${roundedValue}`;
      }
    }
  });

  return result;
}

/**
 * Calculates background colors for zone-based charts using rate data.
 *
 * This function takes zone rate data and converts it to background colors
 * for the heat map visualization. It's used for charts like contact_rate_by_zone,
 * whiff_rate_by_zone, etc.
 *
 * @param zoneData - Object with zone numbers as keys and rate values as values
 * @returns BackgroundColors object with hex color values for each zone
 *
 * @example
 * ```typescript
 * const zoneData = {
 *   "1": 0.083,   // 8.3% rate
 *   "7": 0.167    // 16.7% rate
 * };
 * const colors = calculateZoneColorsFromRates(zoneData);
 * // Returns BackgroundColors with appropriate colors based on rates
 * ```
 */
export function calculateZoneColorsFromRates(
  zoneData: Record<string, number>,
): BackgroundColors {
  const zoneMapping: Record<string, keyof BackgroundColors> = {
    "1": "innerTopLeft",
    "2": "innerTopCenter",
    "3": "innerTopRight",
    "4": "innerBottomLeft",
    "5": "innerBottomRight",
    "6": "innerMiddleLeft",
    "7": "innerMiddleCenter",
    "8": "innerMiddleRight",
    "9": "innerBottomCenter",
    "10": "outerBottomRight",
    "11": "outerTopLeft",
    "12": "outerTopRight",
    "13": "outerBottomLeft",
  };

  const backgroundColors: BackgroundColors = {};

  // Initialize all zones with default color
  Object.values(zoneMapping).forEach((zone) => {
    backgroundColors[zone] = getColorForPercentage(0);
  });

  // Apply colors based on rate data
  Object.entries(zoneData).forEach(([zoneNumber, rate]) => {
    const uiZone = zoneMapping[zoneNumber];
    if (uiZone) {
      const percentage = rate * 100;
      backgroundColors[uiZone] = getColorForPercentage(percentage);
    }
  });

  return backgroundColors;
}

export function calculateZoneColors(points: Points): BackgroundColors {
  // Initialize zone counts
  const zoneCounts: Record<keyof BackgroundColors, number> = {
    outerTopLeft: 0,
    outerTopRight: 0,
    outerBottomLeft: 0,
    outerBottomRight: 0,
    innerTopLeft: 0,
    innerTopCenter: 0,
    innerTopRight: 0,
    innerMiddleLeft: 0,
    innerMiddleCenter: 0,
    innerMiddleRight: 0,
    innerBottomLeft: 0,
    innerBottomCenter: 0,
    innerBottomRight: 0,
  };

  // Count points in each zone
  let totalPoints = 0;
  Object.values(points).forEach((pointArray) => {
    pointArray.forEach((point) => {
      const zone = getZoneForPoint(point.x, point.y);
      if (zone) {
        zoneCounts[zone]++;
        totalPoints++;
      }
    });
  });

  // Calculate percentages and map to colors
  const backgroundColors: BackgroundColors = {};

  // Assign colors based on percentages
  (Object.keys(zoneCounts) as Array<keyof BackgroundColors>).forEach((zone) => {
    const percentage =
      totalPoints > 0 ? (zoneCounts[zone] / totalPoints) * 100 : 0;
    backgroundColors[zone] = getColorForPercentage(percentage);
  });

  return backgroundColors;
}
